<script lang="ts">
  import { onMount } from 'svelte';
  import Dropdown from '../../../Dropdown.svelte';
  import DatePicker from '../../../DateRange.svelte';
  import Button from '../../../Button.svelte';
  import Pagination from '../../../Pagination.svelte';
  import Table from '../../../Table.svelte';
  import SearchInput from '../../../SearchInput.svelte';
  import { ReportService, type DonationRequest, type ReportFilters, type DonationReportResponse } from '../../../../lib/api/admin/donationReport.service';

  // Loading and error states
  let loading = true;
  let error: string | null = null;

  // Donation data
  let donationRequests: DonationRequest[] = [];
  
  // Pagination state
  let currentPage = 1;
  let totalPages = 1;
  let perPage = 10;
  let totalItems = 0;

  // Filter states
  let selectedCharity = '';
  let selectedStatus = '';
  let startDate = '';
  let endDate = '';
  let phrase = '';

  // Table columns configuration
  const columns = [
    { 
      key: 'employer', 
      label: 'Employer',
      render: (value: any, row: DonationRequest) => {
        return `<span>${row.employer?.name || 'N/A'}</span>`;
      }
    },
    { 
      key: 'donation_request_status',
      label: 'Status',
      align: 'center',
      render: (value: any, row: DonationRequest) => {
        const status = row.donation_request_status?.name || 'Unknown';
        return `
          <div class="status-badge ${getStatusClass(status)}">
            ${getStatusIcon(status)}
            <span>${status.toUpperCase()}</span>
          </div>
        `;
      }
    },
    { 
      key: 'total_donation', 
      label: 'Total Donation',
      render: (value: number , row: DonationRequest) => {
        return `<span>${row.leave_hours} Hours</span>`;
      }
    },
    { 
      key: 'created_at', 
      label: 'Date',
      render: (value: string) => formatDate(value)
    }
  ];

  // Format currency
  function formatCurrency(amount: number): string {
    if (amount === undefined || amount === null) return '$0.00';
    return `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  // Format date
  function formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  }

  function getStatusClass(status: string): string {
    switch(status.toLowerCase()) {
      case 'approved': return 'status-completed';
      case 'completed': return 'status-completed';
      case 'pending': return 'status-pending';
      case 'processing': return 'status-pending';
      case 'rejected': return 'status-rejected';
      default: return '';
    }
  }

  function getStatusIcon(status: string): string {
    switch(status.toLowerCase()) {
      case 'approved':
      case 'completed': return `
        <svg class="status-icon" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
        </svg>
      `;
      case 'pending':
      case 'processing': return `
        <svg class="status-icon" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd" />
        </svg>
      `;
      case 'rejected': return `
        <svg class="status-icon" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
        </svg>
      `;
      default: return '';
    }
  }

  // Load donation report data
  async function loadDonationReport() {
    loading = true;
    error = null;

    try {
      // Prepare filters
      const filters: ReportFilters = {
        page: currentPage,
        per_page: perPage,
        sort_by: 'created_at',
        sort_direction: 'desc'
      };

      // Add optional filters if they are set
      if (startDate) filters.start_date = startDate;
      if (endDate) filters.end_date = endDate;
      if (selectedCharity) filters.charity_uuid = selectedCharity;
      if (selectedStatus) filters.donation_request_status_uuid = selectedStatus;
      if (phrase) filters.phrase = phrase;

      // Call API
      const response = await ReportService.generateDonationReport(filters);

      if (response.success && response.data) {
        donationRequests = response.data.donation_requests.data;
        
        // Update pagination
        const paging = response.data.donation_requests.paging;
        currentPage = paging.current_page + 1; // API uses 0-based indexing
        totalPages = paging.last_page;
        perPage = paging.per_page;
        totalItems = paging.total;
        
        console.log('Loaded donation report:', {
          items: donationRequests.length,
          currentPage,
          totalPages,
          totalItems
        });
      } else {
        error = response.message || 'Failed to load donation report';
        donationRequests = [];
      }
    } catch (err: any) {
      console.error('Error loading donation report:', err);
      error = err.message || 'An unexpected error occurred';
      donationRequests = [];
    } finally {
      loading = false;
    }
  }

  // Handle page change
  function handlePageChange(event: CustomEvent<{ page: number }>) {
    currentPage = event.detail.page;
    loadDonationReport();
  }

  // Handle filter change
  function handleFilterChange() {
    currentPage = 1; // Reset to first page when filters change
    loadDonationReport();
  }

  // Handle search input
  function handleSearchInput(event: CustomEvent) {
    phrase = event.detail;
    handleFilterChange();
  }

  // Download reports
  async function downloadAsPDF() {
    try {
      // Prepare filters
      const filters: ReportFilters = {};
      if (startDate) filters.start_date = startDate;
      if (endDate) filters.end_date = endDate;
      if (selectedCharity) filters.charity_uuid = selectedCharity;
      if (selectedStatus) filters.donation_request_status_uuid = selectedStatus;
      if (phrase) filters.phrase = phrase;
      
      const blob = await ReportService.exportDonationReport(filters, 'pdf');
      ReportService.downloadReport(blob, `donation-report-${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (err: any) {
      console.error('Error downloading PDF:', err);
      error = err.message || 'Failed to download PDF';
    }
  }

  async function downloadAsCSV() {
    try {
      // Prepare filters
      const filters: ReportFilters = {};
      if (startDate) filters.start_date = startDate;
      if (endDate) filters.end_date = endDate;
      if (selectedCharity) filters.charity_uuid = selectedCharity;
      if (selectedStatus) filters.donation_request_status_uuid = selectedStatus;
      if (phrase) filters.phrase = phrase;
      
      const blob = await ReportService.exportDonationReport(filters, 'csv');
      ReportService.downloadReport(blob, `donation-report-${new Date().toISOString().split('T')[0]}.csv`);
    } catch (err: any) {
      console.error('Error downloading CSV:', err);
      error = err.message || 'Failed to download CSV';
    }
  }

  // Initialize component
  onMount(() => {
    loadDonationReport();
  });
</script>

<div class="donation-report-container">
  <!-- Filters and Actions Row -->
  <div class="actions">
    <Button variant="secondary" on:click={downloadAsPDF}>
      Download as PDF
    </Button>
    <Button variant="secondary" on:click={downloadAsCSV}>
      Download as CSV
    </Button>
  </div>
  <div class="filters-row">
    <div class="filters">
      <div class="filter-item search-wrapper">
        <SearchInput
          placeholder="Search"
          bind:value={phrase}
          width="250px"
          on:search={handleSearchInput}
        />
      </div>
      <div class="filter-item">
        <DatePicker placeholder="Date Range" bind:startDate bind:endDate />
      </div>
      <div class="filter-item">
        <Button variant="primary" on:click={handleFilterChange}>
          Apply Filters
        </Button>
      </div>
    </div>
   
  </div>

  {#if loading}
    <div class="loading-container">
      <div class="loading-spinner"></div>
      <p>Loading donation report...</p>
    </div>
  {:else if error}
    <div class="error-container">
      <p class="error-message">{error}</p>
      <div class="button-wrapper-donation-report">
        <Button variant="primary" on:click={loadDonationReport}>Try Again</Button>
      </div>
    </div>
  {:else if donationRequests.length === 0}
    <div class="empty-state">
      <svg viewBox="0 0 24 24" width="48" height="48" fill="#9ca3af">
        <path d="M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/>
        <path d="M12 12.59l-2.3-2.3-1.4 1.42L12 15.41l5.7-5.7-1.4-1.42z"/>
      </svg>
      <p>No donation requests found</p>
      <div class="button-wrapper">
        <Button variant="primary" on:click={loadDonationReport}>Reset Filters</Button>
      </div>
    </div>
  {:else}
    <!-- Table using Table component -->
    <Table 
      title="Donation Report"
      columns={columns}
      data={donationRequests}
    />

    <!-- Pagination -->
    <div class="pagination-wrapper">
      <Pagination 
        currentPage={currentPage}
        totalPages={totalPages}
        on:pageChange={handlePageChange}
      />
    </div>
  {/if}
</div>

<style>
  .donation-report-container {
    padding: 20px;
    background-color: #f9fafb;
    border-radius: 8px;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  }

  .filters-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 15px;
  }

  .filters {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
  }

  .filter-item {
    min-width: 180px;
  }

  .search-wrapper {
    min-width: 200px;
  }

  .search-wrapper :global(input) {
    width: 100%;
  }

  .actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-bottom: 10px;
  }

  /* Global selectors for Table component rendered badges */
  :global(.status-badge) {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    border-radius: 9999px;
    font-size: 14px;
    font-weight: 500;
    text-transform: uppercase;
  }

  :global(.status-badge.status-completed) {
    background-color: #d1fae5;
    color: #065f46;
  }

  :global(.status-badge.status-pending) {
    background-color: #fef3c7;
    color: #92400e;
  }

  :global(.status-badge.status-rejected) {
    background-color: #fee2e2;
    color: #b91c1c;
  }

  :global(.status-icon) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: 50%;
  }

  :global(.status-completed .status-icon) {
    color: #10b981;
  }

  :global(.status-pending .status-icon) {
    color: #f59e0b;
  }

  :global(.status-rejected .status-icon) {
    color: #ef4444;
  }

  .pagination-wrapper {
    margin-top: 20px;
    display: flex;
    justify-content: center;
  }

  /* Loading and error states */
  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    margin: 1rem 0;
  }

  .loading-spinner {
    width: 2rem;
    height: 2rem;
    border: 3px solid #f3f4f6;
    border-top: 3px solid #f0b429;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    margin: 1rem 0;
  }

  .error-message {
    color: #dc2626;
    margin-bottom: 1rem;
  }

  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem 2rem;
    text-align: center;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
    margin: 1rem 0;
  }

  .empty-state p {
    color: #6b7280;
    font-size: 1.1rem;
    margin: 1rem 0 1.5rem;
  }

  @media (max-width: 768px) {
    .filters-row {
      flex-direction: column;
      align-items: stretch;
    }

    .filters {
      width: 100%;
    }

    .filter-item {
      width: 100%;
      min-width: unset;
    }

    .actions {
      width: 100%;
      justify-content: space-between;
    }

    :global(.actions button) {
      flex: 0 1 auto; /* Changed from flex: 1 to flex: 0 1 auto */
      text-align: center;
    }
  }

  .button-wrapper-donation-report {
    width: fit-content !important;
  }
</style>